import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import MapPin from '../../components/MapPin'
import { geocodificar, reverseGeocodificar } from '../../lib/geo'
import {
  getDireccionPerfil,
  guardarDireccionPerfil,
  getBarriosActivos,
} from '../../lib/portalClienteApi'

/**
 * /cliente/mi-direccion
 *
 * Permite a CUALQUIER usuario logueado guardar una dirección de referencia
 * sin tener que comprar ni suscribirse. Sirve para:
 *  - Activar el banner Vecinos (5% off colaborativo de la cuadra)
 *  - Pre-llenar el wizard de suscripción / checkout de la tienda
 *  - Que el equipo sepa dónde vive el lead aunque no haya comprado
 */
export default function MiDireccion() {
  const navigate = useNavigate()
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [barrios, setBarrios] = useState([])
  const [ok, setOk] = useState('')
  const [err, setErr] = useState('')

  const [form, setForm] = useState({
    barrio_id: '',
    calle: '',
    numero: '',
    apto: '',
    referencias: '',
  })

  // Preview visual en mapa — geocodificación con debounce
  const [coords, setCoords] = useState(null) // {lat, lng} | null
  const [geocoding, setGeocoding] = useState(false)
  const geocodeTimer = useRef(null)

  useEffect(() => { cargar() }, [])

  // Debounced geocode cuando cambia barrio o calle/numero
  useEffect(() => {
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current)
    const barrioNombre = barrios.find(b => b.id === form.barrio_id)?.nombre
    const calleTrim = form.calle?.trim() || ''
    const numTrim = form.numero?.trim() || ''
    if (!calleTrim || !barrioNombre) {
      setCoords(null)
      return
    }
    geocodeTimer.current = setTimeout(async () => {
      setGeocoding(true)
      const dirCompleta = numTrim ? `${calleTrim} ${numTrim}` : calleTrim
      const q = `${dirCompleta}, ${barrioNombre}`
      const g = await geocodificar(q)
      setGeocoding(false)
      if (g?.lat && g?.lng) setCoords({ lat: g.lat, lng: g.lng })
    }, 700)
    return () => clearTimeout(geocodeTimer.current)
  }, [form.calle, form.numero, form.barrio_id, barrios])

  async function cargar() {
    setCargando(true)
    const [actual, listaBarrios] = await Promise.all([
      getDireccionPerfil(),
      getBarriosActivos(),
    ])
    setBarrios(listaBarrios || [])
    if (actual?.direccion) {
      // Parsear "18 de Julio 1234" → calle="18 de Julio", numero="1234"
      const raw = (actual.direccion || '').trim()
      const m = raw.match(/^(.+?)\s+(\d+[a-zA-Z]?)\s*$/)
      const calleParsed = m ? m[1].trim() : raw
      const numeroParsed = m ? m[2] : ''
      setForm({
        barrio_id: actual.barrio_id || '',
        calle: calleParsed,
        numero: numeroParsed,
        apto: actual.apto || '',
        referencias: actual.referencias || '',
      })
    }
    setCargando(false)
  }

  async function onSubmit(e) {
    e.preventDefault()
    setErr('')
    setOk('')
    if (!form.barrio_id) { setErr('Elegí un barrio.'); return }
    if (!form.calle.trim()) { setErr('Cargá el nombre de la calle.'); return }
    if (!form.numero.trim()) { setErr('Cargá el número de puerta.'); return }

    setGuardando(true)
    try {
      const direccionFull = `${form.calle.trim()} ${form.numero.trim()}`
      await guardarDireccionPerfil({
        barrio_id: form.barrio_id,
        direccion: direccionFull,
        apto: form.apto.trim(),
        referencias: form.referencias.trim(),
      })
      setOk('Dirección guardada.')
      setTimeout(() => navigate('/cliente/vecinos'), 800)
    } catch (e) {
      setErr(e?.message || 'No pudimos guardar la dirección.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <ClienteLayout title="Mi dirección">
      <div className="md-wrap">
        <div className="md-headline">
          <h1>Mi dirección</h1>
          <p>
            Cargá tu dirección y te avisamos cuando tu cuadra llegue a 10 vecinos
            suscriptos para activar el <strong>5% off colaborativo</strong>. Sin
            compromiso de compra — la usamos también para chequear que estés en
            zona de cobertura.
          </p>
        </div>

        {cargando ? (
          <div className="md-loading">Cargando…</div>
        ) : (
          <form className="md-card" onSubmit={onSubmit}>
            <label className="md-field">
              <span className="md-label">Barrio *</span>
              <select
                value={form.barrio_id}
                onChange={(e) => setForm({ ...form, barrio_id: e.target.value })}
                required
              >
                <option value="">Elegí tu barrio…</option>
                {barrios.map(b => (
                  <option key={b.id} value={b.id}>{b.nombre}</option>
                ))}
              </select>
              <span className="md-hint">
                Los 10 barrios donde repartimos hoy. Si no aparece el tuyo, escribinos
                por <Link to="/cliente/soporte">Soporte</Link>.
              </span>
            </label>

            <div className="md-row-calle">
              <label className="md-field md-field-calle">
                <span className="md-label">Calle *</span>
                <input
                  type="text"
                  value={form.calle}
                  onChange={(e) => setForm({ ...form, calle: e.target.value })}
                  placeholder="Ej: Hermanos Gil"
                  required
                />
              </label>
              <label className="md-field md-field-numero">
                <span className="md-label">Nº de puerta *</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.target.value })}
                  placeholder="906"
                  required
                />
              </label>
            </div>

            <div className="md-row-2">
              <label className="md-field">
                <span className="md-label">Apto / Casa</span>
                <input
                  type="text"
                  value={form.apto}
                  onChange={(e) => setForm({ ...form, apto: e.target.value })}
                  placeholder="Ej: 01, A, PB"
                />
              </label>
              <label className="md-field">
                <span className="md-label">Referencias</span>
                <input
                  type="text"
                  value={form.referencias}
                  onChange={(e) => setForm({ ...form, referencias: e.target.value })}
                  placeholder="Ej: timbre 3, edificio rojo"
                />
              </label>
            </div>

            {/* Preview en mapa — Lylian & compañía verifican visualmente */}
            {form.direccion?.trim() && form.barrio_id && (
              <div className="md-mapa">
                <div className="md-mapa-title">
                  📍 Verificá que el pin quede en tu casa
                </div>
                {geocoding && !coords && (
                  <div className="md-mapa-loading">Buscando tu dirección en el mapa…</div>
                )}
                {!geocoding && !coords && (
                  <div className="md-mapa-empty">
                    No pudimos ubicar esa dirección todavía. Revisá la calle y número.
                  </div>
                )}
                {coords && (
                  <MapPin
                    lat={coords.lat}
                    lng={coords.lng}
                    onChange={async (lat, lng) => {
                      setCoords({ lat, lng })
                      // Reverse geocode: solo actualiza el NOMBRE de la calle.
                      // El número de puerta NUNCA se toca — es dato duro del cliente.
                      const rev = await reverseGeocodificar(lat, lng)
                      if (rev?.calle) {
                        setForm(prev => {
                          if (rev.calle === prev.calle) return prev
                          setOk(`Corregimos el nombre de la calle a "${rev.calle}".`)
                          setTimeout(() => setOk(''), 3500)
                          return { ...prev, calle: rev.calle }
                        })
                      }
                    }}
                    height={window.innerWidth <= 540 ? 220 : 280}
                  />
                )}
                <div className="md-mapa-hint">
                  Si no está bien, <strong>arrastrá el pin</strong> o tocá el mapa — la calle se actualiza sola.
                </div>
              </div>
            )}

            {err && <div className="md-err">{err}</div>}
            {ok && <div className="md-ok">{ok}</div>}

            <div className="md-actions">
              <Link to="/cliente/cuenta" className="md-cancel">Cancelar</Link>
              <button
                type="submit"
                className="md-save"
                disabled={guardando || !coords}
                title={!coords ? 'Ajustá la dirección hasta que el pin aparezca en el mapa' : ''}
              >
                {guardando ? 'Guardando…' : (coords ? 'Guardar dirección' : 'Chequeá el pin en el mapa')}
              </button>
            </div>
          </form>
        )}

        <div className="md-info">
          <div className="md-info-icon">🔒</div>
          <div>
            <strong>Tus datos están protegidos.</strong> Sólo los usamos para activar
            el descuento colaborativo de tu cuadra y para chequear cobertura de
            entrega. Más info en nuestra <a href="https://limpack.uy/privacidad" target="_blank" rel="noreferrer">Política de Privacidad</a>.
          </div>
        </div>
      </div>

      <style>{`
        .md-wrap { max-width: 720px; margin: 0 auto; padding: 16px 16px 32px }
        .md-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em }
        .md-headline p {
          font-size: 14px; color: var(--cl-muted); margin-top: 6px;
          line-height: 1.55; max-width: 560px;
        }
        .md-headline strong { color: var(--cl-teal) }

        .md-loading { text-align: center; padding: 40px; color: var(--cl-muted) }

        .md-card {
          margin-top: 20px; padding: 22px;
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 16px;
          display: flex; flex-direction: column; gap: 16px;
        }

        .md-field { display: flex; flex-direction: column; gap: 6px }
        .md-label { font-size: 12.5px; font-weight: 800; color: var(--cl-text); letter-spacing: .01em }
        .md-hint { font-size: 11.5px; color: var(--cl-muted); line-height: 1.4 }
        .md-hint a { color: var(--cl-teal); text-decoration: none }

        .md-field input, .md-field select {
          background: rgba(0,0,0,.25);
          color: var(--cl-text);
          border: 1px solid var(--cl-line);
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14px; font-family: inherit;
          transition: border-color .15s, box-shadow .15s;
        }
        .md-field input:focus, .md-field select:focus {
          outline: 0;
          border-color: var(--cl-teal);
          box-shadow: 0 0 0 3px rgba(0,212,224,.18);
        }
        .md-field input::placeholder { color: var(--cl-muted2, #6b7d92) }

        .md-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px }
        @media (max-width: 540px) { .md-row-2 { grid-template-columns: 1fr } }

        /* Calle + Número: calle mayor, número más chico (~110px) */
        .md-row-calle { display: grid; grid-template-columns: 1fr 110px; gap: 14px }
        @media (max-width: 540px) { .md-row-calle { grid-template-columns: 1fr 90px } }

        .md-mapa {
          display: flex; flex-direction: column; gap: 8px;
          padding: 12px; border-radius: 12px;
          background: rgba(0,168,181,.06);
          border: 1px solid rgba(0,168,181,.18);
        }
        .md-mapa-title {
          font-size: 13px; font-weight: 800; color: var(--cl-text);
        }
        .md-mapa-loading, .md-mapa-empty {
          font-size: 12.5px; color: var(--cl-muted); padding: 8px 4px;
        }
        .md-mapa-empty { color: #fca5a5 }
        .md-mapa-hint {
          font-size: 12px; color: var(--cl-muted); line-height: 1.4;
        }
        .md-mapa-hint strong { color: var(--cl-teal) }
        @media (max-width: 540px) {
          .md-mapa { padding: 10px }
          .md-mapa-title { font-size: 12.5px }
        }

        .md-err {
          background: rgba(248, 113, 113, .12);
          border: 1px solid rgba(248, 113, 113, .35);
          color: #fca5a5;
          padding: 10px 14px; border-radius: 10px;
          font-size: 13px;
        }
        .md-ok {
          background: rgba(34, 197, 94, .12);
          border: 1px solid rgba(34, 197, 94, .35);
          color: #86efac;
          padding: 10px 14px; border-radius: 10px;
          font-size: 13px;
        }

        .md-actions {
          display: flex; gap: 10px; justify-content: flex-end;
          margin-top: 6px;
        }
        .md-cancel {
          padding: 11px 18px; border-radius: 999px;
          font-size: 13.5px; font-weight: 700; text-decoration: none;
          color: var(--cl-muted); border: 1px solid var(--cl-line);
        }
        .md-cancel:hover { color: var(--cl-text); border-color: var(--cl-line2) }
        .md-save {
          padding: 11px 22px; border-radius: 999px;
          background: var(--cl-teal); color: #002a30; border: 0;
          font-size: 13.5px; font-weight: 800; font-family: inherit;
          cursor: pointer; transition: transform .15s, box-shadow .15s;
        }
        .md-save:hover { transform: translateY(-1px); box-shadow: 0 8px 18px rgba(0,212,224,.25) }
        .md-save:disabled { opacity: .6; cursor: not-allowed }

        .md-info {
          margin-top: 18px; padding: 14px 16px;
          background: rgba(0,168,181,.06);
          border: 1px solid rgba(0,168,181,.18);
          border-radius: 12px;
          display: flex; gap: 12px; align-items: flex-start;
          font-size: 12.5px; color: var(--cl-muted); line-height: 1.55;
        }
        .md-info-icon { font-size: 18px; flex-shrink: 0 }
        .md-info strong { color: var(--cl-text) }
        .md-info a { color: var(--cl-teal); text-decoration: none }
      `}</style>
    </ClienteLayout>
  )
}
